/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * ExecutionDetailDrawer
 * Replaces the old cramped dialog with a right-side drawer that shows the
 * execution summary, stop reason, step-by-step trace, and full message log.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';

import { agentApi } from '../../../services/api';
import { formatStoredDateTime } from '../../../utils/datetime';
import { LoadingSpinner } from '../../common';

interface ExecutionDetailDrawerProps {
  executionId: string | null;
  open: boolean;
  onClose: () => void;
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  COMPLETED: 'success',
  ERROR: 'error',
  RUNNING: 'warning',
  CANCELLED: 'default',
};

const formatCredits = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(Number(value))) {
    return '-';
  }
  return `${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} cr`;
};

export const ExecutionDetailDrawer: React.FC<ExecutionDetailDrawerProps> = ({
  executionId,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['execution-detail', executionId],
    queryFn: () => (executionId ? agentApi.getExecution(executionId) : null),
    enabled: !!executionId && open,
  });

  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['execution-messages', executionId],
    queryFn: () => (executionId ? agentApi.getExecutionMessages(executionId) : []),
    enabled: !!executionId && open,
  });

  const loading = detailLoading || messagesLoading;

  return (
    <Drawer
      anchor={isSmDown ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 640, md: 760 },
          maxHeight: { xs: '92vh', sm: '100%' },
        },
      }}
    >
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              Execution details
            </Typography>
            {executionId ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace' }}
              >
                {executionId}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LoadingSpinner />
            </Box>
          ) : !detail ? (
            <Alert severity="info">No data for this execution.</Alert>
          ) : (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04) }}
              >
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box>
                      <Chip
                        size="small"
                        label={detail.status}
                        color={STATUS_COLORS[detail.status] || 'default'}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {detail.duration_seconds?.toFixed(2)}s
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Tokens</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {detail.total_tokens?.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Credit Usage</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCredits(detail.credit_usage ?? detail.credits_used)}
                    </Typography>
                  </Grid>
                  {detail.result?.iterations ? (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Iterations</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {detail.result.iterations}
                      </Typography>
                    </Grid>
                  ) : null}
                  {detail.started_at ? (
                    <Grid size={{ xs: 12, sm: 9 }}>
                      <Typography variant="caption" color="text.secondary">Started</Typography>
                      <Typography variant="body2">
                        {formatStoredDateTime(detail.started_at, undefined, '-')}
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>

              <Alert
                severity={
                  detail.status === 'ERROR'
                    ? 'error'
                    : detail.status === 'CANCELLED'
                      ? 'warning'
                      : detail.result?.max_iterations_reached
                        ? 'warning'
                        : detail.status === 'RUNNING'
                          ? 'info'
                          : 'success'
                }
              >
                {detail.status === 'ERROR' && (
                  <>
                    <strong>Error occurred.</strong>{' '}
                    <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {detail.error || 'Unknown error'}
                    </Box>
                  </>
                )}
                {detail.status === 'CANCELLED' && 'Execution cancelled before completion.'}
                {detail.status === 'RUNNING' && 'Execution is still in progress.'}
                {detail.status === 'COMPLETED' && detail.result?.max_iterations_reached && (
                  <>
                    Reached max iterations ({detail.result.iterations}). Task may be partially
                    complete.
                  </>
                )}
                {detail.status === 'COMPLETED' && !detail.result?.max_iterations_reached && (
                  <>Completed normally after {detail.result?.iterations || '—'} iteration(s).</>
                )}
              </Alert>

              {detail.steps && detail.steps.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Steps ({detail.steps.length})
                  </Typography>
                  <Stack spacing={1}>
                    {[...detail.steps]
                      .sort((a: any, b: any) => a.step_number - b.step_number)
                      .map((step: any) => {
                        const isTool = step.step_type === 'TOOL_CALL';
                        const color = isTool
                          ? 'info.main'
                          : step.step_type === 'REASONING'
                            ? 'warning.main'
                            : step.step_type === 'OBSERVATION'
                              ? 'secondary.main'
                              : step.step_type === 'RESPONSE'
                                ? 'success.main'
                                : 'grey.400';
                        return (
                          <Paper
                            key={step.id}
                            variant="outlined"
                            sx={{ p: 1.5, borderLeft: '3px solid', borderColor: color }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 0.75 }}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                size="small"
                                label={isTool ? step.tool_name || 'Tool call' : step.step_type}
                                color={isTool ? 'info' : 'default'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Step {step.step_number} · {step.tokens_used} tok
                              </Typography>
                            </Stack>

                            {!isTool && step.content ? (
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  p: 1,
                                  fontSize: '0.75rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  bgcolor: isDark ? 'grey.900' : 'grey.50',
                                  borderRadius: 1,
                                  maxHeight: 220,
                                  overflow: 'auto',
                                }}
                              >
                                {step.content}
                              </Box>
                            ) : null}

                            {isTool ? (
                              <Stack spacing={0.75}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                    Input
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      p: 1,
                                      fontSize: '0.7rem',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      bgcolor: isDark ? 'grey.900' : 'grey.50',
                                      borderRadius: 1,
                                      maxHeight: 180,
                                      overflow: 'auto',
                                    }}
                                  >
                                    {step.tool_input ? JSON.stringify(step.tool_input, null, 2) : 'null'}
                                  </Box>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                    Output
                                  </Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      p: 1,
                                      fontSize: '0.7rem',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      bgcolor:
                                        step.tool_output?.success === false
                                          ? isDark
                                            ? 'error.900'
                                            : 'error.50'
                                          : isDark
                                            ? 'success.900'
                                            : 'success.50',
                                      borderRadius: 1,
                                      maxHeight: 180,
                                      overflow: 'auto',
                                    }}
                                  >
                                    {step.tool_output ? JSON.stringify(step.tool_output, null, 2) : 'null'}
                                  </Box>
                                </Box>
                              </Stack>
                            ) : null}

                            {step.messages_sent && step.messages_sent.length > 0 ? (
                              <Accordion disableGutters elevation={0} sx={{ mt: 1 }}>
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  sx={{
                                    minHeight: 36,
                                    px: 1,
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    borderRadius: 1,
                                    '& .MuiAccordionSummary-content': { my: 0.5 },
                                  }}
                                >
                                  <Typography variant="caption" fontWeight={700}>
                                    Context sent to LLM · {step.messages_sent.length} msgs
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 1 }}>
                                  <Stack spacing={0.75}>
                                    {step.messages_sent.map((msg: any, i: number) => (
                                      <Paper
                                        key={i}
                                        variant="outlined"
                                        sx={{
                                          p: 1,
                                          borderLeft: '3px solid',
                                          borderColor:
                                            msg.role === 'system'
                                              ? 'info.main'
                                              : msg.role === 'user'
                                                ? 'primary.main'
                                                : msg.role === 'assistant'
                                                  ? 'success.main'
                                                  : 'grey.400',
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          label={(msg.role || 'unknown').toUpperCase()}
                                          sx={{ height: 18, fontSize: '0.6rem' }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            mt: 0.5,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.72rem',
                                          }}
                                        >
                                          {typeof msg.content === 'string'
                                            ? msg.content
                                            : JSON.stringify(msg.content, null, 2)}
                                        </Typography>
                                      </Paper>
                                    ))}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            ) : null}
                          </Paper>
                        );
                      })}
                  </Stack>
                </Box>
              ) : null}

              {messages && messages.length > 0 ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <ChatIcon fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Conversation
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {messages.map((msg: any) => (
                      <Paper
                        key={msg.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderLeft: '3px solid',
                          borderColor:
                            msg.role === 'USER'
                              ? 'primary.main'
                              : msg.role === 'ASSISTANT'
                                ? 'success.main'
                                : 'grey.500',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={msg.role}
                            color={
                              msg.role === 'USER'
                                ? 'primary'
                                : msg.role === 'ASSISTANT'
                                  ? 'success'
                                  : 'default'
                            }
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatStoredDateTime(msg.created_at, undefined, '-')}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {msg.content}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ExecutionDetailDrawer;
